import React, { Component, Fragment } from 'react';
import { AddButton, PrimaryButton } from 'view/components/button';
import { Heading } from 'view/components/text';
import ROUTES from 'src/routes';
import { Link } from '@rexlabs/react-whereabouts';
import projectsModel from 'data/models/entities/projects';
import { query, withQuery } from '@rexlabs/model-generator';
import List from '@rexlabs/list';
import Box from '@rexlabs/box';
import _ from 'lodash';
import { Portal } from '@rexlabs/portal';
import ProjectArchiveListItem from 'view/components/list/project-archive-list-item';
import ProjectArchiveListHeader from 'view/components/list/project-archive-list-header';
import { Grid, Column } from '@rexlabs/grid';
import SectionColumn from 'view/components/grid/section-column';
import { styled, StyleSheet } from '@rexlabs/styling';
import LoadingLayout from 'view/layouts/loading';
import withAction from 'view/containers/with-action';
import withError from 'view/containers/with-error';

import Breadcrumb from '../../components/breadcrumb/breadcrumb';
import { MARGINS } from 'src/theme';

const defaultStyles = StyleSheet({
  container: {
    backgroundColor: 'white',
    width: '100%'
  },
  header: {
    margin: '2rem 2rem',
    justifyContent: 'space-between'
  },
  buttons: {
    display: 'inline-flex',
    '> *': {
      margin: MARGINS.TINY
    }
  },
  breadcrumb: {
    marginLeft: '0.8rem',
    marginTop: '0.8rem'
  }
});

const projectsQuery = query`{
  ${projectsModel} (archived: true, include: "settings") {
    id
    account_id
    sub_region_id
    title
    density
    website
    developer
    sales_office_address
    suburb
    postcode
    status
    local_government_area
    sales_agency
    precinct_structure_plan
    image_id
    estimated_lots
    estimated_number_of_stages
    estimated_start_date
    estimated_completion_date
    archived_at
    trashed_at
    active
    sales_volume_offset
    summary
    settings
    created_at
  }
}`;

@withAction.withPropName('actionModal')
@withError.withPropName('errorModal')
@withQuery(projectsQuery)
@styled(defaultStyles)
class ArchivedProjectsScreen extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showModal: false,
      showAlertModal: false,
      showErrorModal: false,
      showRecoverModal: false,
      checked: {}
    };

    this.updateProjects = this.updateProjects.bind(this);
  }

  updateProjects(recover) {
    const { projects, actionModal, errorModal } = this.props;

    // Filter down to the checked projects
    const checkedProjects = _.map(this.state.checked, (value, key) => {
      return { id: key, value: value };
    }).filter((value) => value.value);

    const promises = recover
      ? checkedProjects.map((project) =>
          projects.recoverFromArchive({
            id: project.id
          })
        )
      : checkedProjects.map((project) =>
          projects.trash({
            id: project.id
          })
        );

    // Execute all the requests
    Promise.all(promises)
      .then(() => {
        projects.refreshList();
        this.setState({ checked: {} });
        actionModal.close();
      })
      .catch((e) => {
        actionModal.close();
        errorModal.open(e);
        projects.refreshList();
      });
  }

  render() {
    const {
      styles: s,
      projects,
      actionModal: { Action },
      errorModal: { Error }
    } = this.props;
    const projectList = _.get(projects, 'list.items', []);
    return _.get(projects, 'list.status') === 'loading' ? (
      <LoadingLayout />
    ) : (
      <Fragment>
        <Portal target="header">
          <Box
            style={{ height: '100%', alignItems: 'center', padding: '4rem' }}
            flex={1}
          >
            <Link to={ROUTES.APP.PROJECTS_CURRENT.NEW_PROJECT}>
              {({ onClick }) => (
                <AddButton onClick={onClick}>Add Project</AddButton>
              )}
            </Link>
          </Box>
        </Portal>
        <Grid gutter={0} columns={12}>
          <Column width={12}>
            <Box {...s('breadcrumb')}>
              <Breadcrumb
                crumbs={[
                  {
                    label: 'Dashboard',
                    route: '/app/projects/current'
                  },
                  {
                    label: 'Archived'
                  }
                ]}
              />
            </Box>
          </Column>
        </Grid>
        <Grid gutter={0} columns={12}>
          <SectionColumn width={12}>
            <Box {...s('container')}>
              <Box flex={1} {...s('header')}>
                <Heading>Project Archive</Heading>
                {_.filter(this.state.checked, (value) => value).length > 0 && (
                  <Box {...s('buttons')}>
                    <PrimaryButton
                      green
                      onClick={() => {
                        this.props.actionModal.open(
                          () => this.updateProjects(true),
                          'Are you sure you want to recover this Project Listing(s) and all associated worksheets?',
                          'Recover Project(s)'
                        );
                      }}
                    >
                      Recover Project(s)
                    </PrimaryButton>
                    <PrimaryButton
                      red
                      onClick={() => {
                        this.props.actionModal.open(
                          () => this.updateProjects(false),
                          'Are you sure you want to trash this Project Listing(s) and all associated worksheets?',
                          'Trash Project(s)',
                          true
                        );
                      }}
                    >
                      Trash Project(s)
                    </PrimaryButton>
                  </Box>
                )}
              </Box>
              <List
                items={_.get(projects, 'list.items', [])}
                isLoading={_.get(projects, 'list.status') === 'loading'}
                endReached={_.get(projects, 'list.pagination.endReached')}
                Header={() => (
                  <ProjectArchiveListHeader
                    checked={
                      _.filter(this.state.checked, (value) => value).length ===
                      projectList.length
                    }
                    onSelectAll={() => {
                      if (
                        _.filter(this.state.checked, (value) => value)
                          .length === projectList.length
                      ) {
                        this.setState({
                          checked: {}
                        });
                      } else {
                        const toggledState = projectList.reduce((obj, item) => {
                          return { ...obj, [item.id]: true };
                        }, {});
                        this.setState({
                          checked: toggledState
                        });
                      }
                    }}
                  />
                )}
                renderItem={(item) => {
                  return (
                    <ProjectArchiveListItem
                      project={item}
                      projects={this.props.projects}
                      checked={this.state.checked[item.id]}
                      toggle={() =>
                        this.setState({
                          checked: {
                            ...this.state.checked,
                            ...{
                              [item.id]: !this.state.checked[item.id]
                            }
                          }
                        })
                      }
                    />
                  );
                }}
              />
              {!_.get(projects, 'list.pagination.endReached', true) && (
                <Box
                  flex={1}
                  style={{
                    justifyContent: 'center',
                    alignItems: 'center',
                    margin: '1rem'
                  }}
                >
                  <PrimaryButton
                    green
                    onClick={() => projects.fetchMore()}
                    isLoading={_.get(projects, 'list.status') === 'fetching'}
                    isDisabled={_.get(projects, 'list.status') === 'fetching'}
                  >
                    Load More
                  </PrimaryButton>
                </Box>
              )}
            </Box>
          </SectionColumn>
        </Grid>
        <Action />
        <Error />
      </Fragment>
    );
  }
}

export default ArchivedProjectsScreen;
